import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Icon, Message, Popup, Segment, Loader } from 'semantic-ui-react';
import { VictoryAnimation, VictoryLabel, VictoryPie } from 'victory';
import { doGetEquipmentMinimumSpend } from '../../actions/equipment-actions.js';
import './minimum-spend-monitor.css';

const formatDollarsExact = (amount) => {
  if (amount == null) return '$0.00';
  return amount.toLocaleString(navigator.language, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatMonthYear = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleString('default', { month: 'long', year: 'numeric' });
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('default', { month: 'short', day: 'numeric' });
};

const formatLongDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('default', { month: 'long', day: 'numeric' });
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonth = (monthStr, delta) => {
  const [year, month] = monthStr.split('-').map(Number);
  const d = new Date(year, month - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export class MinimumSpendMonitor extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      percent: 0,
      data: this.getData(0),
      selectedMonth: getCurrentMonth(),
    };
  }

  componentDidMount() {
    this.fetchMonth(this.state.selectedMonth);
    this.animateTimeout = window.setTimeout(this.progressToState, 1500);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.locationId !== this.props.locationId && this.props.locationId) {
      const month = getCurrentMonth();
      this.setState({ selectedMonth: month });
      this.fetchMonth(month);
    }
    if (prevProps.percent !== this.props.percent) {
      window.clearTimeout(this.animateTimeout);
      this.animateTimeout = window.setTimeout(this.progressToState, 500);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.animateTimeout);
  }

  fetchMonth = (month) => {
    if (!this.props.locationId) return;
    this.props.actions.doGetEquipmentMinimumSpend({
      location_id: this.props.locationId,
      month,
    });
  };

  handlePrevMonth = () => {
    const prev = shiftMonth(this.state.selectedMonth, -1);
    this.setState({ selectedMonth: prev });
    this.fetchMonth(prev);
  };

  handleNextMonth = () => {
    const current = getCurrentMonth();
    if (this.state.selectedMonth >= current) return;
    const next = shiftMonth(this.state.selectedMonth, 1);
    this.setState({ selectedMonth: next });
    this.fetchMonth(next);
  };

  progressToState = () => {
    this.setState({
      percent: this.props.percent || 0,
      data: this.getData(this.props.percent || 0),
    });
  };

  getData(percent) {
    const capped = Math.min(percent, 100);
    return [
      { x: 1, y: capped },
      { x: 2, y: 100 - capped },
    ];
  }

  getFillColor(percent) {
    if (percent >= 100) return '#007a65';
    if (percent > 80) return '#009975';
    if (percent > 60) return '#00a98c';
    if (percent > 40) return '#33baa3';
    return '#66cbba';
  }

  render() {
    const { minimumSpend, spend, shortfall, percent, loading, periodStart, periodEnd, orderCutoffDate } =
      this.props;
    const isCurrentMonth = this.state.selectedMonth >= getCurrentMonth();

    if (loading) {
      return (
        <Segment className="minimum-spend-monitor">
          <Loader active inline="centered" />
        </Segment>
      );
    }

    if (minimumSpend == null && !this.props.error) {
      return null;
    }

    if (this.props.error) {
      return (
        <div className="minimum-spend-monitor">
          <Message warning compact>
            <p>Unable to load spend data. Please try again later.</p>
          </Message>
        </div>
      );
    }

    const fillColor = this.getFillColor(percent || 0);

    return (
      <div className="minimum-spend-monitor">
        <div className="monitor-month-selector">
          <button className="month-nav-btn" onClick={this.handlePrevMonth}>
            <Icon name="chevron left" />
          </button>
          <span className="month-label">
            {formatMonthYear(periodStart || this.state.selectedMonth + '-01')}
          </span>
          <button
            className="month-nav-btn"
            onClick={this.handleNextMonth}
            disabled={isCurrentMonth}
          >
            <Icon name="chevron right" />
          </button>
        </div>
        {periodStart && periodEnd && (
          <div className="monitor-period">
            {formatShortDate(periodStart)} – {formatShortDate(periodEnd)}
          </div>
        )}
        <div className="monitor-content">
          <div className="monitor-chart">
            <svg viewBox="0 0 300 300" width="100%" height="100%">
              <VictoryPie
                standalone={false}
                animate={{ duration: 1000 }}
                width={300}
                height={300}
                data={this.state.data}
                innerRadius={120}
                cornerRadius={({ datum }) => (datum.x === 1 ? 20 : 0)}
                labels={() => null}
                style={{
                  data: {
                    fill: (d) => {
                      if (d.datum.x === 2) return '#e8e8e8';
                      return fillColor;
                    },
                  },
                }}
              />
              <VictoryAnimation duration={1000} data={this.state}>
                {(newState) => (
                  <VictoryLabel
                    textAnchor="middle"
                    verticalAnchor="middle"
                    x={150}
                    y={150}
                    text={`${Math.round(newState.percent)}%`}
                    style={{
                      fontSize: 42,
                      fontFamily: 'Roboto, Helvetica, sans-serif',
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </VictoryAnimation>
            </svg>
          </div>
          <div className="monitor-details">
            <div className="monitor-stat">
              <span className="stat-label">
                Monthly Minimum
                <Popup
                  trigger={<Icon name="info circle" style={{ marginLeft: 4, opacity: 0.5, fontSize: '0.85em' }} />}
                  content="Orders for minimum spend must be delivered within the month. The submission window allows time for processing and delivery."
                  size="small"
                  position="top center"
                />
              </span>
              <span className="stat-value">{formatDollarsExact(minimumSpend)}</span>
            </div>
            <div className="monitor-stat">
              <span className="stat-label">Spent This Month</span>
              <span className="stat-value" style={{ color: fillColor }}>
                {formatDollarsExact(spend)}
              </span>
            </div>
            {shortfall > 0 && (
              <div className="monitor-stat shortfall">
                <span className="stat-label">Remaining to Minimum</span>
                <span className="stat-value shortfall-value">
                  {formatDollarsExact(shortfall)}
                </span>
              </div>
            )}
            {isCurrentMonth && orderCutoffDate && (() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const [cy, cm, cd] = orderCutoffDate.split('-').map(Number);
              const cutoff = new Date(cy, cm - 1, cd);
              const isPastCutoff = today > cutoff;
              return (
                <div className="monitor-cutoff">
                  {isPastCutoff
                    ? `Orders submitted by ${formatLongDate(orderCutoffDate)} have been applied to this month's minimum.`
                    : `Place orders by ${formatLongDate(orderCutoffDate)} to count toward this month's minimum.`}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    );
  }
}

MinimumSpendMonitor.propTypes = {
  locationId: PropTypes.number,
  minimumSpend: PropTypes.number,
  spend: PropTypes.number,
  shortfall: PropTypes.number,
  percent: PropTypes.number,
  vendorId: PropTypes.number,
  periodStart: PropTypes.string,
  periodEnd: PropTypes.string,
  orderCutoffDate: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  actions: PropTypes.shape({
    doGetEquipmentMinimumSpend: PropTypes.func.isRequired,
  }).isRequired,
};

function mapStateToProps(state) {
  const ms = state.equipment.minimumSpend;
  return {
    locationId: state.locations.open.id,
    minimumSpend: ms.minimum_spend,
    spend: ms.spend,
    shortfall: ms.shortfall,
    percent: ms.percent,
    vendorId: ms.vendor_id,
    periodStart: ms.period_start,
    periodEnd: ms.period_end,
    orderCutoffDate: ms.order_cutoff_date,
    loading: ms.loading,
    error: ms.error,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ doGetEquipmentMinimumSpend }, dispatch),
  };
}

function areStatesEqual(prev, next) {
  return (
    prev.equipment.minimumSpend === next.equipment.minimumSpend &&
    prev.locations.open.id === next.locations.open.id
  );
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatesEqual,
})(MinimumSpendMonitor);
